import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { employeeActions } from '@nxsys/store/slices/employee/reducer/employeeSlice';
import { GetExpensePotEmployeeListParams } from '@nxsys/store/slices/employee/types';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

type ViewExpenseModalProps = {
  isOpen: boolean;
  toggle: () => void;
  employeeId: string;
};

const ViewExpenseModal: React.FC<ViewExpenseModalProps> = ({
  isOpen,
  toggle,
  employeeId,
}: ViewExpenseModalProps) => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch<AppDispatch>();

  const { expensePotList } = useSelector(
    (state: AppState) => state.employeeStore,
  );
  const { list, pagination, loading, queryParams } = expensePotList;

  useEffect(() => {
    if (isOpen) {
      handleFetchData({
        page: 0,
        size: 20,
      });
    }
  }, [isOpen]);

  const handleFetchData = (params: GetExpensePotEmployeeListParams) => {
    const newParams = {
      ...params,
      id: employeeId,
    };
    dispatch(employeeActions.getExpensePotEmployeeList(newParams));
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const columns: any = [
    {
      key: 'taxPeriod',
      title: messages['common.taxPeriod'],
      render: (value: any) =>
        `${value?.taxPeriod || ''}/${value?.taxYear || ''}`,
    },
    {
      key: 'openingBalance',
      title: messages['common.openingBalance'],
      dataIndex: 'openingBalance',
    },
    {
      key: 'claimed',
      title: messages['common.claimed'],
      dataIndex: 'claimed',
    },
    {
      key: 'paid',
      title: messages['common.paid'],
      dataIndex: 'paid',
    },
    {
      key: 'closingBalance',
      title: messages['common.closingBalance'],
      dataIndex: 'closingBalance',
    },
  ];

  return (
    <AppModalContainer
      title={messages['common.expensePay']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.close'],
          variant: 'outlined',
          onClick: toggle,
        },
      ]}
      width="65vw"
    >
      <AppsContent>
        <AppTableContainer
          data={list || []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 1000, y: 700 }}
          titleEmpty={messages[`common.noData`]}
          columns={columns}
          onChangePagination={onChangePagination}
        />
      </AppsContent>
    </AppModalContainer>
  );
};

export default ViewExpenseModal;
